import { Check, ExpandLess, ExpandMore, Sync } from '@tempo-io/sui-icons';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Loop } from '@/common/api/types/loops';
import { resolveEffectiveRunStatus } from '@/features/agentic-insights/components/data/runLifecycle';
import { useDismissOnOutside } from '@/features/agentic-insights/components/experimental/shared/ui/useDismissOnOutside';
import type { AggregateLoopRun } from '../data/aggregateFindings';
import { rawFindingCount } from '../data/aggregateFindings';
import type { LoopScope } from '../data/loopScope';
import { TYPE } from '../language';

interface LoopSwitcherProps {
  scope: LoopScope;
  navLoopId: number;
  loops: readonly Loop[];
  /** Keyed by loop id, for each row's finding count and running spinner. */
  runsByLoopId: ReadonlyMap<number, AggregateLoopRun>;
  totalFindings: number;
  onChange: (scope: LoopScope) => void;
  /** Told whenever the popover opens or closes, so the page can defer fanning out a `runs/latest`
   * per loop (an org-wide fetch) until there is actually a reason to — see `ExperimentalInsightsPage`. */
  onOpenChange?: (open: boolean) => void;
}

const CountCell = ({
  loopId,
  runsByLoopId,
}: {
  loopId: number;
  runsByLoopId: ReadonlyMap<number, AggregateLoopRun>;
}) => {
  const { t } = useTranslation();
  const entry = runsByLoopId.get(loopId);

  // A loop mid-run will have a number shortly, which is a different fact from having none — worth a
  // glyph of its own. Never-run and failed both collapse to the same dash: for a reader deciding
  // where to look next they amount to the same thing, and the run-state screen says which on arrival.
  //
  // Checked against the *effective* status, not the raw one: a run whose heartbeat has gone stale
  // is `failed` everywhere else on this surface (and has stopped polling), so a raw `running` here
  // would spin forever on exactly the run the rest of the page has already given up on.
  if (entry?.run && resolveEffectiveRunStatus(entry.run, Date.now()) === 'running') {
    return (
      <Sync
        className="size-3 shrink-0 animate-spin text-icon-neutral-secondary"
        aria-label={t(($) => $.loopAgenticInsights.experimental.loopSwitcher.generating)}
      />
    );
  }

  const count = entry ? rawFindingCount(entry) : null;
  return (
    <span
      className={`${TYPE.metaValue} min-w-4 text-right ${count === null ? 'text-text-neutral-tertiary' : 'text-text-neutral-primary'}`}
    >
      {count === null ? '—' : count}
    </span>
  );
};

/**
 * Which loop's run is being read — Loop Intelligence's `LoopScopeFilter` mechanics (all loops plus
 * every loop, the active row tinted and checked, re-picking the active row a no-op that just closes),
 * translated into this surface's own chrome: an underlined inline trigger behind a mono field label
 * rather than LI's outlined `sui-button`, because nothing else on this surface is a bordered button.
 *
 * Every loop the reader can see is offered, not only the ones with findings — a loop with nothing to
 * say still has to be reachable, or it reads as missing rather than as empty. There is no per-loop
 * access model today (`LoopScopeFilter.tsx`'s own note), so the list is exactly `useAllLoopsQuery()`'s
 * set and nothing is filtered out on that basis.
 */
export const LoopSwitcher = ({
  scope,
  navLoopId,
  loops,
  runsByLoopId,
  totalFindings,
  onChange,
  onOpenChange,
}: LoopSwitcherProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  const setOpenAndNotify = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  useDismissOnOutside(open, containerRef, () => setOpenAndNotify(false));

  const selectedName = scope === null ? undefined : loops.find((loop) => loop.id === scope)?.name;

  // Scoped to a specific loop, but the org's loop list hasn't resolved that id yet: showing "All
  // loops" here would be wrong (the scope is a specific loop, not All), and showing a placeholder
  // that then snaps to the real name is the jitter this surface avoids everywhere else. Nothing
  // beats a wrong or shifting label, so the whole control waits.
  if (scope !== null && selectedName === undefined) return null;

  const label =
    scope === null
      ? t(($) => $.loopAgenticInsights.experimental.loopSwitcher.allLoops)
      : selectedName;

  const select = (next: LoopScope) => {
    setOpenAndNotify(false);
    // Re-picking the active scope is a no-op beyond closing the popover — matching LoopScopeFilter,
    // which closes this same way to avoid firing a change (and, once tracked, an analytics event) for
    // a scope that did not actually change.
    if (next !== scope) onChange(next);
  };

  return (
    <span ref={containerRef} className="relative inline-flex items-center gap-1.5">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? panelId : undefined}
        aria-label={t(($) => $.loopAgenticInsights.experimental.loopSwitcher.ariaLabel)}
        data-testid="agentic-x-loop-switcher"
        onClick={() => setOpenAndNotify(!open)}
        // text-xs matches its row neighbours (the objective pills, their "…" overflow, and
        // List/Details) — this button set no size of its own and inherited the surface's larger
        // base size, which is what put "All loops" visibly bigger than "All objectives" and off
        // the row's optical centre.
        className="flex shrink-0 items-center gap-1.5 text-xs whitespace-nowrap underline decoration-border-neutral-bold decoration-1 underline-offset-4 hover:decoration-text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-brand"
      >
        <Sync className="size-3.5 shrink-0 text-icon-brand-primary" aria-hidden="true" />
        <span className="max-w-40 truncate">{label}</span>
        {open ? (
          <ExpandLess className="size-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <ExpandMore className="size-3.5 shrink-0" aria-hidden="true" />
        )}
      </button>

      {open ? (
        <span
          id={panelId}
          role="listbox"
          aria-label={t(($) => $.loopAgenticInsights.experimental.loopSwitcher.ariaLabel)}
          data-testid="agentic-x-loop-switcher-menu"
          className="absolute top-full left-0 z-20 mt-2 flex max-h-80 w-64 flex-col overflow-y-auto rounded-md border border-border-neutral bg-bg-surface-overlay py-1 shadow-lg"
        >
          <span className="flex items-center justify-between gap-2 px-3 py-1.5">
            <span className={TYPE.section}>
              {t(($) => $.loopAgenticInsights.experimental.loopSwitcher.sectionLabel)}
            </span>
            <span className={TYPE.section}>
              {t(($) => $.loopAgenticInsights.experimental.loopSwitcher.findingsHeading)}
            </span>
          </span>
          <button
            type="button"
            role="option"
            aria-selected={scope === null}
            data-testid="agentic-x-loop-switcher-all"
            onClick={() => select(null)}
            className={`flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-bg-neutral-subtle-hovered ${
              scope === null ? 'bg-bg-brand-subtler text-text-brand-primary' : ''
            }`}
          >
            <span>{t(($) => $.loopAgenticInsights.experimental.loopSwitcher.allLoops)}</span>
            <span className="flex items-center gap-2">
              <span className={TYPE.metaValue}>{totalFindings}</span>
              {scope === null ? (
                <Check className="size-4" aria-hidden="true" />
              ) : (
                <span className="size-4" />
              )}
            </span>
          </button>
          <span className="my-1 h-px bg-border-neutral-subtle" aria-hidden="true" />
          {loops.map((loop) => {
            const isSelected = scope === loop.id;
            return (
              <button
                key={loop.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-testid={`agentic-x-loop-switcher-option-${loop.id}`}
                onClick={() => select(loop.id)}
                className={`flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-bg-neutral-subtle-hovered ${
                  isSelected ? 'bg-bg-brand-subtler text-text-brand-primary' : ''
                }`}
              >
                <span className="flex min-w-0 items-baseline gap-1.5 truncate">
                  <span
                    className={`truncate ${loop.id === navLoopId ? 'font-medium text-text-neutral-primary' : ''}`}
                  >
                    {loop.name}
                  </span>
                  {loop.id === navLoopId ? (
                    // The bold above is the only signal that this is the reader's own loop, and
                    // bold is purely visual — this is the non-visual equivalent, for anyone using a
                    // screen reader, replacing the "· on" text this row used to carry instead.
                    <span className="sr-only">
                      {t(
                        ($) => $.loopAgenticInsights.experimental.loopSwitcher.onThisLoopAccessible,
                      )}
                    </span>
                  ) : null}
                </span>
                <span className="flex items-center gap-2">
                  <CountCell loopId={loop.id} runsByLoopId={runsByLoopId} />
                  {isSelected ? (
                    <Check className="size-4" aria-hidden="true" />
                  ) : (
                    <span className="size-4" />
                  )}
                </span>
              </button>
            );
          })}
        </span>
      ) : null}
    </span>
  );
};
